import { useRef } from "react";
import RefinedCardPreview from "./RefinedCardPreview";

const SWIPE_DISTANCE_THRESHOLD = 48;
const SWIPE_AXIS_RATIO = 1.2;

function PencilIcon() {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function getCarouselOffset(index, selectedIndex, totalCards) {
  if (totalCards <= 0) {
    return 0;
  }

  let offset = index - selectedIndex;

  if (offset > totalCards / 2) {
    offset -= totalCards;
  }

  if (offset < -totalCards / 2) {
    offset += totalCards;
  }

  return offset;
}

function getCarouselCardStyle(offset) {
  if (offset === 0) {
    return {
      opacity: 1,
      pointerEvents: "auto",
      transform: "translate3d(-50%, -50%, 90px) rotateY(0deg) scale(0.96)",
      zIndex: 30,
    };
  }

  if (offset === -1) {
    return {
      opacity: 0.78,
      pointerEvents: "auto",
      transform:
        "translate3d(calc(-50% - clamp(132px, 38vw, 286px)), -50%, -140px) rotateY(-52deg) scale(0.8)",
      zIndex: 12,
    };
  }

  if (offset === 1) {
    return {
      opacity: 0.78,
      pointerEvents: "auto",
      transform:
        "translate3d(calc(-50% + clamp(132px, 38vw, 286px)), -50%, -140px) rotateY(52deg) scale(0.8)",
      zIndex: 12,
    };
  }

  return {
    opacity: 0,
    pointerEvents: "none",
    transform: "translate3d(-50%, -50%, -220px) rotateY(0deg) scale(0.62)",
    zIndex: 1,
  };
}

function ChooseRefinedCardView({
  cardVariants,
  selectedVariantId,
  onSelectVariant,
  result,
  rawThought,
  getVariantQuestion,
  getVariantTranslation,
  getVariantTranslations,
  isInteractionDisabled = false,
  isEditEligible = false,
  onEditCard,
}) {
  const swipeStartRef = useRef(null);
  const selectedCardIndex = Math.max(
    0,
    cardVariants.findIndex((variant) => variant.id === selectedVariantId)
  );
  const totalCards = cardVariants.length;

  const selectPreviousCard = () => {
    const previousIndex = (selectedCardIndex - 1 + totalCards) % totalCards;
    onSelectVariant(cardVariants[previousIndex].id);
  };

  const selectNextCard = () => {
    const nextIndex = (selectedCardIndex + 1) % totalCards;
    onSelectVariant(cardVariants[nextIndex].id);
  };

  const handleCarouselTouchStart = (event) => {
    if (totalCards <= 1) {
      return;
    }

    const touch = event.touches[0];

    swipeStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
  };

  const handleCarouselTouchEnd = (event) => {
    if (totalCards <= 1 || !swipeStartRef.current) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = touch.clientY - swipeStartRef.current.y;

    swipeStartRef.current = null;

    if (
      Math.abs(deltaX) < SWIPE_DISTANCE_THRESHOLD ||
      Math.abs(deltaX) < Math.abs(deltaY) * SWIPE_AXIS_RATIO
    ) {
      return;
    }

    if (deltaX < 0) {
      selectNextCard();
    } else {
      selectPreviousCard();
    }
  };

  const handleCarouselTouchCancel = () => {
    swipeStartRef.current = null;
  };
  const selectedVariantLabel = cardVariants[selectedCardIndex]?.label || "";

  return (
    <>
      <div
        className="relative z-10 mx-auto mb-1 mt-1 h-[390px] w-full max-w-[780px] select-none [perspective:1250px] [touch-action:pan-y] sm:my-3 sm:h-[585px]"
        onTouchStart={handleCarouselTouchStart}
        onTouchEnd={handleCarouselTouchEnd}
        onTouchCancel={handleCarouselTouchCancel}
      >
        <div className="pointer-events-none absolute inset-x-[12%] bottom-8 top-12 rounded-[999px] bg-[radial-gradient(circle_at_50%_50%,rgba(244,217,78,0.18),rgba(106,174,37,0.08)_38%,transparent_70%)] blur-2xl" />

        <div className="absolute inset-0 [transform-style:preserve-3d]">
          {cardVariants.map((variant, index) => {
            const isSelected = variant.id === selectedVariantId;
            const carouselOffset = getCarouselOffset(
              index,
              selectedCardIndex,
              totalCards
            );
            const variantQuestion = getVariantQuestion(variant.id);
            const variantTranslation =
              getVariantTranslation?.(variant.id) || "";
            const variantTranslations =
              getVariantTranslations?.(variant.id) || result?.translations || [];

            return (
              <div
                key={variant.id}
                className="absolute left-1/2 top-1/2 w-[260px] transition-[opacity,transform,filter] duration-500 ease-out [transform-style:preserve-3d] sm:w-[340px]"
                style={getCarouselCardStyle(carouselOffset)}
              >
                <button
                  type="button"
                  onClick={() => onSelectVariant(variant.id)}
                  disabled={isInteractionDisabled}
                  aria-label={`Show ${variant.label} card`}
                  aria-pressed={isSelected}
                  className="block w-full text-left outline-none focus-visible:ring-4 focus-visible:ring-[#f4d94e]/45"
                >
                  <div
                    className={`transition duration-500 ${
                      isSelected
                        ? "brightness-105"
                        : "blur-[3px] brightness-70 saturate-[0.72]"
                    }`}
                  >
                    <RefinedCardPreview
                      result={{
                        ...result,
                        refinedQuestion: variantQuestion,
                        translation: variantTranslation,
                        translations: variantTranslations,
                      }}
                      draftQuestion={rawThought}
                      toneLabel=""
                      size={isSelected ? "large" : "default"}
                      showSparkles={isSelected}
                    />
                  </div>
                </button>

                {isSelected && isEditEligible && onEditCard ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onEditCard();
                    }}
                    disabled={isInteractionDisabled}
                    className="absolute bottom-[6%] left-[11%] z-40 flex h-9 w-9 items-center justify-center rounded-full border border-[#fff7b5] bg-[#fffdf1] p-0 leading-none text-[#245f07] shadow-[0_8px_18px_rgba(0,0,0,0.34),0_2px_5px_rgba(78,67,24,0.26)] transition hover:bg-white hover:shadow-[0_10px_24px_rgba(0,0,0,0.4),0_2px_6px_rgba(78,67,24,0.3)] active:scale-95 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f4d94e]"
                    style={{ bottom: "calc(6% - 4px)" }}
                    aria-label="Edit wording"
                    title="Edit wording"
                  >
                    <PencilIcon />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="relative z-50 mx-auto grid w-full max-w-[250px] grid-cols-[2.25rem_minmax(0,1fr)_2.25rem] items-center gap-4 sm:max-w-[315px]">
        {totalCards > 1 ? (
          <button
            type="button"
            onClick={selectPreviousCard}
            disabled={isInteractionDisabled}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/12 bg-white/[0.06] text-2xl leading-none text-white/76 shadow-[0_12px_30px_rgba(0,0,0,0.2)] backdrop-blur-xl transition hover:bg-white/[0.1] hover:text-white active:scale-95"
            aria-label="Show previous card"
          >
            ‹
          </button>
        ) : (
          <div className="h-9 w-9 shrink-0" aria-hidden="true" />
        )}

        <div className="flex min-w-0 flex-col items-center gap-2">
          <p
            className="min-w-0 text-center text-xs font-black uppercase tracking-[0.22em] text-white/82 drop-shadow-[0_0_14px_rgba(255,255,255,0.22)] sm:text-sm"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {selectedVariantLabel}
          </p>

          {totalCards > 1 ? (
            <div
              role="group"
              className="flex items-center justify-center gap-1.5"
              aria-label="Choose a card"
            >
              {cardVariants.map((variant, index) => {
                const isSelected = variant.id === selectedVariantId;

                return (
                  <button
                    key={variant.id}
                    type="button"
                    onClick={() => onSelectVariant(variant.id)}
                    disabled={isInteractionDisabled}
                    aria-label={`Select ${variant.label} card`}
                    aria-current={isSelected ? "step" : undefined}
                    className={`h-2 w-2 rounded-full transition ${
                      isSelected
                        ? "bg-[#f4d94e] shadow-[0_0_12px_rgba(244,217,78,0.55)]"
                        : "bg-[#5f6258] hover:bg-[#777b70]"
                    }`}
                  >
                    <span className="sr-only">
                      Card {index + 1} of {totalCards}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        {totalCards > 1 ? (
          <button
            type="button"
            onClick={selectNextCard}
            disabled={isInteractionDisabled}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/12 bg-white/[0.06] text-2xl leading-none text-white/76 shadow-[0_12px_30px_rgba(0,0,0,0.2)] backdrop-blur-xl transition hover:bg-white/[0.1] hover:text-white active:scale-95"
            aria-label="Show next card"
          >
            ›
          </button>
        ) : (
          <div className="h-9 w-9 shrink-0" aria-hidden="true" />
        )}
      </div>

      <div className="sr-only" aria-live="polite">
        Showing card {selectedCardIndex + 1} of {cardVariants.length}
      </div>
    </>
  );
}

export default ChooseRefinedCardView;
